//import '@reach/alert-dialog/styles.css';
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationCircle, FaToggleOff, FaToggleOn } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { UserProfile } from '../../../../@posturinn/client-react';
import { setActiveModal } from '../../../../actions/modalActions';
import { createPaymentCardError, getPaymentCard } from '../../../../actions/paymentActions';
import { updateUserProfile } from '../../../../actions/userProfileActions';
import { CONFIRMATION, PAYMENT_OPTION_MODAL } from '../../../../constants/documentTypes/modals/modals';
import usePaymentErrorFromQuery from '../../../../hooks/usePaymentErrorFromQuery';
import { ReactComponent as IconBin } from '../../../../icons/bin.svg';
import { ReactComponent as IconCard } from '../../../../icons/card_default.svg';
import { ReactComponent as IconMasterCard } from '../../../../icons/card_mastercard.svg';
import { ReactComponent as IconVisa } from '../../../../icons/card_visa.svg';
import { ReactComponent as IconPosi } from '../../../../icons/posi.svg';
import IPaymentReducer from '../../../../interfaces/reducers/paymentReducer';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../../reducers';
import { Column, Columns } from '../../../base/grid';
import MyShipmentsTileLoader from '../../../base/misc/myShipmentsTileLoader';
import { A, H4, H6, P } from '../../../base/typography';
import { RawLoader } from '../../../loading/loading';
import * as StyledModals from '../../modals/myShipmentsModals.styles';
import * as StyledMyShipments from '../../myShipments/myShipments.styles';
import * as StyledSettings from '../settings.styles';

interface Props {}

/**
 * expiryYear comes back as either a 2- or 4-digit value, and as a number or a string depending on
 * which Mobiz endpoint produced the card, so normalise it to a full year before comparing.
 */
const toFullYear = (expiryYear: number | string): number | null => {
	const digits = String(expiryYear ?? '').trim();

	if (!/^(\d{2}|\d{4})$/.test(digits)) return null;

	const value = Number(digits);

	return digits.length === 2 ? 2000 + value : value;
};

const lastTwoDigits = (expiryYear: number | string): string => String(expiryYear ?? '').slice(-2);

const PaymentCard: FC<Props> = () => {
	const { t } = useTranslation('myShipments');
	const dispatch = useDispatch<any>();
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile, modalSubmitPending } = userProfileReducer;

	usePaymentErrorFromQuery(createPaymentCardError);

	const paymentReducer: IPaymentReducer = useSelector<IStore, IPaymentReducer>(store => store.paymentReducer);
	const { paymentCardLoading, paymentCard, paymentCardError } = paymentReducer;

	useEffect(() => {
		if (paymentCard === null) dispatch(getPaymentCard());
	}, [paymentCard, dispatch]);

	const isExpired = card => {
		const now = new Date();
		const expiryYear = toFullYear(card?.expiryYear);
		const expiryMonth = Number(card?.expiryMonth);

		// Without a usable expiry date, treat the card as active rather than warning the user wrongly.
		if (expiryYear === null || !Number.isFinite(expiryMonth)) return false;
		if (expiryYear < now.getFullYear()) return true;

		// getMonth() is 0-based while expiryMonth is 1-based, so a card expiring this month is still valid.
		return expiryYear === now.getFullYear() && expiryMonth <= now.getMonth();
	};

	const setAutomaticPayments = () => {
		const newUserProfile: UserProfile = userProfile.clone();
		newUserProfile.permissionToMakeAutomaticPayments = !newUserProfile.permissionToMakeAutomaticPayments;
		newUserProfile.paymentCardId = paymentCard?.id;
		dispatch(updateUserProfile(newUserProfile));
	};

	return (
		<>
			<StyledSettings.HeaderSub>
				<H4>{t('page-settings.paymentOptions')}</H4>
			</StyledSettings.HeaderSub>
			{paymentCardError && (
				<StyledModals.ErrorMessage>
					<FaExclamationCircle />
					{paymentCardError}
				</StyledModals.ErrorMessage>
			)}
			{paymentCardLoading && <MyShipmentsTileLoader />}
			{!paymentCardLoading &&
				(!paymentCard ? (
					<StyledSettings.LinkWrapper onClick={() => dispatch(setActiveModal(PAYMENT_OPTION_MODAL))}>
						<StyledMyShipments.ShipmentCard simpleIcon red>
							<IconCard />
							{t('page-settings.addPaymentOption')}
						</StyledMyShipments.ShipmentCard>
					</StyledSettings.LinkWrapper>
				) : (
					<Columns>
						<Column tablet={1 / 2} desktopLarge={2 / 3}>
							<StyledSettings.Card>
								<StyledSettings.CardHeader>
									<StyledSettings.PaymentMethodInfo>
										{{ Visa: <IconVisa />, MasterCard: <IconMasterCard /> }[
											paymentCard.cardType
										] || <IconCard />}
										<div>
											<H6>{paymentCard.cardNumber}</H6>
											<span>
												{paymentCard.expiryMonth}/{lastTwoDigits(paymentCard.expiryYear)}
											</span>
										</div>
									</StyledSettings.PaymentMethodInfo>
									<StyledSettings.PaymentMethodStatus expired={isExpired(paymentCard)}>
										{isExpired(paymentCard) ? `${t('expired')}` : `${t('active')}`}
									</StyledSettings.PaymentMethodStatus>
								</StyledSettings.CardHeader>
								<StyledSettings.CardFooter>
									<StyledSettings.CardFooterActions>
										{/*<A onClick={() => dispatch(setActiveModal(PAYMENT_OPTION_MODAL))}>*/}
										{/*	<IconPencil title={t('change')}/>*/}
										{/*</A>*/}
										<A
											onClick={() =>
												dispatch(
													setActiveModal(
														CONFIRMATION,
														t('page-settings.deleteCard'),
														'DELETE_CARD',
														paymentCard.id
													)
												)
											}>
											<IconBin title={t('delete')} />
										</A>
									</StyledSettings.CardFooterActions>
								</StyledSettings.CardFooter>
							</StyledSettings.Card>
						</Column>
						<Column tablet={1 / 2} desktopLarge={1 / 3}>
							<StyledSettings.Card>
								<StyledSettings.CardHeader automatic={userProfile.permissionToMakeAutomaticPayments}>
									<StyledSettings.CardIcon>
										<IconPosi />
									</StyledSettings.CardIcon>
									<span>
										<P black>{t('automaticPayments')}</P>
										{t('page-settings.automaticPaymentsInfo') as string}
									</span>
								</StyledSettings.CardHeader>
								<StyledSettings.CardFooter>
									{modalSubmitPending && <RawLoader small />}
									{!modalSubmitPending && (
										<StyledSettings.CardFooterRadio
											selected={userProfile.permissionToMakeAutomaticPayments}>
											<A onClick={() => setAutomaticPayments()}>
												{userProfile.permissionToMakeAutomaticPayments ? (
													<>
														<FaToggleOn />
														{t('page-settings.disableAutomaticPayments')}
													</>
												) : (
													<>
														<FaToggleOff />
														{t('page-settings.enableAutomaticPayments')}
													</>
												)}
											</A>
										</StyledSettings.CardFooterRadio>
									)}
								</StyledSettings.CardFooter>
							</StyledSettings.Card>
						</Column>
					</Columns>
				))}
		</>
	);
};

export default PaymentCard;
